// pdbreader.ts ↔ game/mempdbreader.cpp (+ the pdb structs from mpshared/packfile.h).
//
// The web client preloads whole .pdb files and reads records out of memory
// (MemPdbReader); the TS client does the same from a fetched ArrayBuffer. All pdb
// header/entry fields are BIG-endian. Decompressed records are cached per record —
// the C++ routes this through gcam (CacheMgr handles, LRU-evictable); in TS the
// cache is a plain Map and the GC/eviction concern disappears (memmgr is
// scope-excluded), but the BYTES are identical: single-shot DecompressToMemory is
// the canonical semantics (DecompressToCache chunking produces the same output).

import type { CompressionHeader } from './decompress.ts';
import { kcbCompressionHeader, readCompressionHeader, DecompressToMemory } from './decompress.ts';

// packfile.h:36-57 — DatabaseHdrType is 80 bytes; record entries start at 78
// (sizeof(hdr) - sizeof(recordList.firstEntry)), 8 bytes each (LocalID + attr + uid3).
const kcbDatabaseHdr = 80;
const koffRecordEntries = 78;
const kcbRecordEntry = 8;

export interface RecordEntry {
  localChunkID: number;
  attributes: number;
}

export class MemPdbReader {
  private m_pb: Uint8Array | null = null;
  private m_cRecs = 0;
  private recordCache = new Map<number, Uint8Array>(); // ↔ m_aphcRecordData/gcam

  get cRecs(): number {
    return this.m_cRecs;
  }

  /** pdb name field (header bytes 0-31, NUL-terminated). */
  name(): string {
    let s = '';
    for (let i = 0; i < 32 && this.m_pb![i] !== 0; i++) s += String.fromCharCode(this.m_pb![i]);
    return s;
  }

  // game/mempdbreader.cpp:21-68 — Open: the host file read becomes "bytes in hand"
  Open(pb: Uint8Array): boolean {
    if (this.m_pb !== null) throw new Error('MemPdbReader.Open: already open');
    if (pb.length < kcbDatabaseHdr) return false;
    this.m_pb = pb;
    this.m_cRecs = (pb[76] << 8) | pb[77]; // recordList.numRecords (BE word @76)
    return true;
  }

  Close(): void {
    this.m_pb = null;
    this.m_cRecs = 0;
    this.recordCache.clear();
  }

  // game/mempdbreader.cpp:89-129
  GetRecordEntry(nRec: number, cRec: number): { entries: RecordEntry[]; cb: number } | null {
    if (nRec + cRec > this.m_cRecs) return null;
    const pb = this.m_pb!;
    const readEntry = (i: number): RecordEntry => {
      const off = koffRecordEntries + i * kcbRecordEntry;
      return {
        localChunkID:
          ((pb[off] << 24) | (pb[off + 1] << 16) | (pb[off + 2] << 8) | pb[off + 3]) >>> 0,
        attributes: pb[off + 4],
      };
    };
    const entries: RecordEntry[] = [];
    for (let i = 0; i < cRec; i++) entries.push(readEntry(nRec + i));
    let cb: number;
    if (nRec + cRec === this.m_cRecs) {
      cb = pb.length - entries[cRec - 1].localChunkID;
    } else {
      // size = next record's start − this run's start (cpp reads one extra entry)
      cb = readEntry(nRec + cRec).localChunkID - entries[0].localChunkID;
    }
    return { entries, cb };
  }

  // game/mempdbreader.cpp:131-139 — returns {coh, offBytes} (0/null = failure)
  GetCompressionHeader(nRec: number): { coh: CompressionHeader; offBytes: number } | null {
    const re = this.GetRecordEntry(nRec, 1);
    if (re === null) return null;
    const off = re.entries[0].localChunkID;
    return { coh: readCompressionHeader(this.m_pb!, off), offBytes: off + kcbCompressionHeader };
  }

  // game/mempdbreader.cpp:80-87
  GetRecordSize(nRec: number): number | null {
    const ch = this.GetCompressionHeader(nRec);
    return ch === null ? null : ch.coh.cbUncompressed;
  }

  /** The decompressed (or raw, if stored uncompressed) record bytes — the shared
   *  backing for ReadRecord/MapRecord (mempdbreader.cpp:141-230). */
  private recordBytes(nRec: number): Uint8Array | null {
    const cached = this.recordCache.get(nRec);
    if (cached !== undefined) return cached;
    const ch = this.GetCompressionHeader(nRec);
    if (ch === null) return null;
    const { coh, offBytes } = ch;
    const bytes = coh.fCompressed
      ? DecompressToMemory(this.m_pb!, offBytes, coh)
      : this.m_pb!.subarray(offBytes, offBytes + coh.cbUncompressed);
    this.recordCache.set(nRec, bytes);
    return bytes;
  }

  // game/mempdbreader.cpp:141-181 — copy cb bytes at offset n of the record
  ReadRecord(nRec: number, n: number, cb: number, pv: Uint8Array): boolean {
    const bytes = this.recordBytes(nRec);
    if (bytes === null || n + cb > bytes.length) return false;
    pv.set(bytes.subarray(n, n + cb));
    return true;
  }

  // game/mempdbreader.cpp:183-230 — MapRecord/UnmapRecord collapse to a view (GC)
  MapRecord(nRec: number): Uint8Array | null {
    return this.recordBytes(nRec);
  }
}
